"use client"

import Link from "next/link"
import { useEffect, useMemo, useRef, useState } from "react"
import { useAuth } from "@/lib/auth-context"

/* ───────────────────────────────────────────────────────────────────────────
   /try — try-before-signup. Drop a CSV (≤100 rows on this client demo),
   we run the same eight client-side validators we ship in production
   (regex + curated lists), render results across six tabs, and gate
   download / persistence behind a free signup.

   The full server pipeline (live MX, suppression list, history) lives
   inside /dashboard once the user signs up.
   ─────────────────────────────────────────────────────────────────────────── */

const T = {
  bg:        "#FAF7EE",
  bgSoft:    "#F4F1E8",
  bgInk:     "#0A0A0A",
  surface:   "#FFFFFF",
  surface2:  "#FBF8F0",
  surface3:  "#F0EDE3",
  hair:      "rgba(10,10,10,0.08)",
  hair2:     "rgba(10,10,10,0.14)",
  ink:       "#0A0A0A",
  ink2:      "rgba(10,10,10,0.62)",
  ink3:      "rgba(10,10,10,0.42)",
  ink4:      "rgba(10,10,10,0.28)",
  acc:       "#0F5132",
  accDeep:   "#0A3D26",
  accLite:   "#1F7A4E",
  accChip:   "#DFF5E5",
  accGlow:   "rgba(15,81,50,0.18)",
  accSoft:   "rgba(15,81,50,0.06)",
  warn:      "#B85C00",
  warnSoft:  "rgba(184,92,0,0.10)",
  bad:       "#A02B1F",
  badSoft:   "rgba(160,43,31,0.10)",
  blue:      "#2D4A8A",
  blueSoft:  "rgba(45,74,138,0.10)",
}

const FM = "'JetBrains Mono', ui-monospace, monospace"
const MAXW = "max-w-[1240px]"

// Anonymous preview budget per device. See ingestText comments.
const PREVIEW_BUDGET_KEY  = "databridge:anon-preview-rows"
const PREVIEW_BUDGET_CAP  = 500

/* ───────────────────────────────────────────────────────────────────────────
   Validators (client-side only) — same logic shipped in production
   for syntax / role / disposable / typo / TLD checks. The remaining
   three (MX / suppression / historical) require the server, so for
   anonymous users we mark them as "approximate" with a small caveat.
   ─────────────────────────────────────────────────────────────────────────── */

const ROLES = ["info", "support", "admin", "sales", "contact", "hello", "help", "no-reply", "noreply", "billing", "marketing", "team"]
const DISPOSABLES = ["mailinator.com", "tempmail.com", "10minutemail.com", "guerrillamail.com", "yopmail.com", "trashmail.com", "fake-mail.net", "throwawaymail.com"]
const TYPOS = ["gmial.com", "gnail.com", "hotnail.com", "yaho.com", "outloo.com", "gmal.com", "gmail.co", "yahooo.com", "hotmial.com"]
const GOOD_TLDS = /\.(com|net|org|io|co|dev|app|ai|sh|edu|gov|mil)$/

type Verdict = "deliverable" | "role" | "disposable" | "typo" | "syntax" | "tld"

type Row = {
  email: string
  raw: string
  verdict: Verdict
  reason: string
  caughtBy: string
}

function validate(emailRaw: string): Row {
  const email = emailRaw.trim().toLowerCase()
  const syntax = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)
  const local = email.split("@")[0] || ""
  const domain = email.split("@")[1] || ""

  if (!syntax) {
    return { email, raw: emailRaw, verdict: "syntax", reason: "Invalid syntax", caughtBy: "Syntax" }
  }
  if (DISPOSABLES.includes(domain)) {
    return { email, raw: emailRaw, verdict: "disposable", reason: "Throwaway provider", caughtBy: "Disposable" }
  }
  if (TYPOS.includes(domain)) {
    return { email, raw: emailRaw, verdict: "typo", reason: `Likely typo on ${domain}`, caughtBy: "Typo guard" }
  }
  if (ROLES.includes(local)) {
    return { email, raw: emailRaw, verdict: "role", reason: `Role-based mailbox (${local}@)`, caughtBy: "Role-based" }
  }
  if (!GOOD_TLDS.test(domain)) {
    return { email, raw: emailRaw, verdict: "tld", reason: "Suspicious TLD", caughtBy: "TLD trust" }
  }
  return { email, raw: emailRaw, verdict: "deliverable", reason: "All checks passed", caughtBy: "—" }
}

const VERDICT_META: Record<Verdict, { label: string; color: string; soft: string; tag: string }> = {
  deliverable: { label: "Deliverable", color: T.acc,  soft: T.accChip,  tag: "OK"   },
  role:        { label: "Role-based",  color: T.warn, soft: T.warnSoft, tag: "WARN" },
  disposable:  { label: "Disposable",  color: T.bad,  soft: T.badSoft,  tag: "FAIL" },
  typo:        { label: "Typo",        color: T.warn, soft: T.warnSoft, tag: "WARN" },
  syntax:      { label: "Bad syntax",  color: T.bad,  soft: T.badSoft,  tag: "FAIL" },
  tld:         { label: "Risky TLD",   color: T.warn, soft: T.warnSoft, tag: "WARN" },
}

/* ───────────────────────────────────────────────────────────────────────────
   PAGE
   ─────────────────────────────────────────────────────────────────────────── */

type Phase = "drop" | "processing" | "done" | "gated"
type Tab = "overview" | "rows" | "validators" | "mapper" | "compare"

export default function TryPage() {
  const [phase, setPhase] = useState<Phase>("drop")
  const [filename, setFilename] = useState<string>("")
  const [filesize, setFilesize] = useState<number>(0)
  const [rows, setRows] = useState<Row[]>([])
  const [progress, setProgress] = useState(0)
  const [stage, setStage] = useState(0)
  const [tab, setTab] = useState<Tab>("overview")
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const stages = ["Parsing CSV", "Syntax", "Role-based", "Disposable", "Typo guard", "TLD trust", "Suppression*", "Historical*", "Building report"]

  // Anonymous preview budget — closes the abuse path where someone
  // splits a 1k list into ten 100-row drops and gets the cleaned
  // verdicts for free without ever signing up. localStorage is honor
  // system (private window resets it), but the cap is enough friction
  // that determined abusers hit it before extracting useful volume.
  //
  // CAP_ROWS chosen so a normal evaluator (1–4 drops to "try the
  // product") never trips it, while splitting a real list to dodge
  // the free tier becomes gated at the sixth chunk.
  //
  // Sample loads bypass the meter — the 20-row demo is a marketing
  // surface and counting it against the user makes no sense.

  function ingestText(text: string, name: string, size: number, opts?: { skipBudget?: boolean }) {
    setError(null)
    setFilename(name)
    setFilesize(size)

    // Budget gate — see PREVIEW_BUDGET_CAP rationale up top. Sample
    // loads skip this (marketing surface, not real usage).
    if (!opts?.skipBudget && typeof window !== "undefined") {
      let used = 0
      try { used = parseInt(window.localStorage.getItem(PREVIEW_BUDGET_KEY) || "0", 10) || 0 } catch {}
      if (used >= PREVIEW_BUDGET_CAP) {
        setPhase("gated")
        return
      }
    }

    // tiny CSV parser — finds an "email"-ish column then yields up to 100 emails.
    const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0)
    if (lines.length === 0) {
      setError("Empty file. Upload a CSV with at least one email column.")
      return
    }

    const header = lines[0].split(/[,;\t]/).map((h) => h.trim().toLowerCase().replace(/^["']|["']$/g, ""))
    const emailIdx = header.findIndex((h) => /(^|_)email|^mail$|correo|e-mail/.test(h))
    let emails: string[] = []

    if (emailIdx >= 0) {
      // proper CSV with a header row
      for (let i = 1; i < Math.min(lines.length, 101); i++) {
        const cells = parseCsvLine(lines[i])
        const v = cells[emailIdx]
        if (v && v.includes("@")) emails.push(v)
      }
    } else {
      // headerless or mailto-only — treat every cell that looks like an email
      for (let i = 0; i < Math.min(lines.length, 101); i++) {
        const cells = parseCsvLine(lines[i])
        for (const c of cells) {
          if (c.includes("@") && c.length < 200) {
            emails.push(c)
            break
          }
        }
      }
    }

    if (emails.length === 0) {
      setError("No email column detected. Add a column called \"email\" and try again.")
      return
    }

    runValidation(emails)
  }

  function parseCsvLine(line: string): string[] {
    const out: string[] = []
    let buf = ""
    let inQuote = false
    for (let i = 0; i < line.length; i++) {
      const c = line[i]
      if (c === '"' && line[i + 1] === '"' && inQuote) { buf += '"'; i++; continue }
      if (c === '"') { inQuote = !inQuote; continue }
      if ((c === "," || c === ";" || c === "\t") && !inQuote) { out.push(buf); buf = ""; continue }
      buf += c
    }
    out.push(buf)
    return out.map((s) => s.trim().replace(/^["']|["']$/g, ""))
  }

  function runValidation(emails: string[]) {
    setPhase("processing")
    setProgress(0)
    setStage(0)

    // animate progress + run validators in stages so the UI reads as
    // a real pipeline (matches /preview/aperture/try timing)
    const dur = 2400
    const start = performance.now()
    let raf = 0
    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / dur)
      setProgress(p)
      setStage(Math.min(stages.length - 1, Math.floor(p * stages.length)))
      if (p < 1) {
        raf = requestAnimationFrame(tick)
      } else {
        const validated = emails.slice(0, 100).map(validate)
        setRows(validated)
        setPhase("done")

        // Increment the anonymous preview budget by the actual count
        // we processed. Sample loads are filename-stamped and skip
        // the meter — counting our own marketing demo against the
        // user makes no sense. Real drops always count, including
        // sub-100 partial files.
        if (filename !== "sample-q2-prospects.csv" && typeof window !== "undefined") {
          try {
            const prev = parseInt(window.localStorage.getItem(PREVIEW_BUDGET_KEY) || "0", 10) || 0
            window.localStorage.setItem(PREVIEW_BUDGET_KEY, String(prev + validated.length))
          } catch { /* quota / privacy mode — silently skip the meter */ }
        }
      }
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }

  function onFile(file: File | undefined) {
    if (!file) return
    const reader = new FileReader()
    reader.onload = (e) => {
      const text = String(e.target?.result || "")
      ingestText(text, file.name, file.size)

      // Persist the raw upload so /dashboard can resume processing
      // after the user signs up. Without this, anyone who dropped a
      // CSV on /try, saw the 100-row preview, and signed up had to
      // re-upload the SAME file at /dashboard — a friction step that
      // killed conversion from the cold-email funnel.
      //
      // sessionStorage gives ~5–10 MB per origin, which fits any list
      // up to roughly 100k rows at average row size. Bigger files
      // silently skip the cache and fall back to the existing
      // re-upload flow, so the optimisation never breaks anything.
      try {
        const MAX_PERSIST_BYTES = 4 * 1024 * 1024 // 4 MB hard cap; leaves headroom under the ~5 MB browser limit
        if (typeof window !== "undefined" && file.size <= MAX_PERSIST_BYTES) {
          const payload = {
            name:      file.name,
            size:      file.size,
            text,
            savedAt:   Date.now(),
          }
          window.sessionStorage.setItem("databridge:pending-upload", JSON.stringify(payload))
        }
      } catch {
        // QuotaExceeded or any other storage error → silently no-op.
        // The user keeps their preview; the dashboard just won't
        // auto-resume and they'll re-upload manually like before.
      }
    }
    reader.onerror = () => setError("Couldn't read the file. Try again.")
    reader.readAsText(file)
  }

  function loadSample() {
    const sample = [
      "email,name,company",
      "founder@stripe.com,Patrick,Stripe",
      "info@acme.io,Mailbox,Acme",
      "test@mailinator.com,Demo,—",
      "sara@gmial.com,Sara,Gmial",
      "ceo@linear.app,Karri,Linear",
      "support@notion.so,Bot,Notion",
      "marco@mercury.com,Marco,Mercury",
      "noreply@vercel.com,System,Vercel",
      "alex@plaid.co,Alex,Plaid",
      "demo@10minutemail.com,Burn,—",
      "user@example.xyz,Test,Sketchy",
      "founder@brex.com,Henrique,Brex",
      "marketing@figma.com,Team,Figma",
      "hello@retool.com,Sales,Retool",
      "ben@anthropic.com,Ben,Anthropic",
      "naomi@ramp.com,Naomi,Ramp",
      "raj@loom.com,Raj,Loom",
      "emma@brex.com,Emma,Brex",
      "test@yaho.com,Typo,—",
      "kai@notion.so,Kai,Notion",
    ].join("\n")
    ingestText(sample, "sample-q2-prospects.csv", new Blob([sample]).size, { skipBudget: true })
  }

  return (
    <div
      className="min-h-screen w-full"
      style={{ background: T.bg, color: T.ink }}
    >
      <link
        rel="stylesheet"
        href="https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600;700&display=swap"
      />
      <style>{`
        @keyframes try-pulse { 0%,100% { opacity: 1 } 50% { opacity: 0.4 } }
        @keyframes try-rise {
          from { opacity: 0; transform: translateY(8px) }
          to   { opacity: 1; transform: translateY(0) }
        }
        ::selection { background: ${T.accChip}; color: ${T.ink} }
      `}</style>

      {/* ambient warm halo */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0"
        style={{
          background: `
            radial-gradient(900px 500px at 70% -8%, ${T.accGlow}, transparent 60%),
            radial-gradient(700px 400px at 5% 110%, rgba(45,74,138,0.06), transparent 60%)
          `,
        }}
      />

      <main className={`relative ${MAXW} mx-auto px-4 sm:px-6 lg:px-10 pt-6 sm:pt-10 pb-24 sm:pb-32`}>
        {phase === "drop" && (
          <DropZone
            inputRef={fileInputRef}
            onFile={onFile}
            onSample={loadSample}
            error={error}
          />
        )}

        {phase === "processing" && (
          <Processing progress={progress} stage={stage} stages={stages} filename={filename} filesize={filesize} />
        )}

        {phase === "gated" && <PreviewBudgetGate />}

        {phase === "done" && (
          <ResultView
            filename={filename}
            filesize={filesize}
            rows={rows}
            tab={tab}
            setTab={setTab}
            onAnother={() => { setPhase("drop"); setRows([]); setError(null); setProgress(0); setStage(0); }}
          />
        )}
      </main>

      {/* `isSample` flips the CTA copy so the sticky bar (and the
          locked-rows nudge inside RowsTab) doesn't say "keep this
          report" when the report is the bundled 20-row demo — there
          is no real report to keep, only a "now try yours" pivot. */}
      {phase === "done" && <StickyCta isSample={filename === "sample-q2-prospects.csv"} />}
    </div>
  )
}

function ArrowR({ size = 13 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

/* ───────────────────────────────────────────────────────────────────────────
   Drop zone
   ─────────────────────────────────────────────────────────────────────────── */

function DropZone({
  inputRef,
  onFile,
  onSample,
  error,
}: {
  inputRef: React.RefObject<HTMLInputElement | null>
  onFile: (f: File | undefined) => void
  onSample: () => void
  error: string | null
}) {
  const [drag, setDrag] = useState(false)

  return (
    <section className="relative">
      <div className="text-center max-w-[640px] mx-auto">
        <p
          style={{
            fontFamily: FM, fontSize: 11, letterSpacing: "0.18em",
            textTransform: "uppercase", color: T.ink3, fontWeight: 600,
          }}
        >
          <span style={{ color: T.acc, marginRight: 8 }}>—</span>
          Free preview · 100 rows · no signup
        </p>
        <h1
          className="mt-3 leading-[1.02] tracking-[-0.035em]"
          style={{ fontSize: "clamp(1.55rem, 5.2vw, 3rem)", fontWeight: 800 }}
        >
          Drop a CSV. Get a verdict in{" "}
          <em style={{ color: T.acc, fontStyle: "italic", fontWeight: 700 }}>seconds.</em>
        </h1>
        <p className="mt-3" style={{ fontSize: 13.5, color: T.ink2, lineHeight: 1.55 }}>
          We&rsquo;ll run eight validators on the first 100 rows. The full set —
          including live MX and audit-trail export — unlocks free with signup.
        </p>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept=".csv,text/csv"
        className="hidden"
        onChange={(e) => onFile(e.target.files?.[0])}
      />

      <div
        onDragEnter={(e) => { e.preventDefault(); setDrag(true) }}
        onDragOver={(e) => { e.preventDefault(); setDrag(true) }}
        onDragLeave={(e) => { e.preventDefault(); setDrag(false) }}
        onDrop={(e) => {
          e.preventDefault()
          setDrag(false)
          onFile(e.dataTransfer.files?.[0])
        }}
        onClick={() => inputRef.current?.click()}
        className="relative mt-10 mx-auto rounded-2xl cursor-pointer transition-all"
        style={{
          maxWidth: 720,
          background: drag ? T.accChip : T.surface,
          border: `2px dashed ${drag ? T.acc : T.hair2}`,
          boxShadow: drag
            ? `0 0 0 6px ${T.accSoft}, 0 30px 70px -28px ${T.accGlow}`
            : `0 1px 0 rgba(255,255,255,0.6) inset, 0 30px 70px -28px rgba(10,10,10,0.18)`,
        }}
      >
        <div className="px-5 py-10 sm:px-8 sm:py-14 text-center">
          <span
            className="inline-flex items-center justify-center mb-5"
            style={{
              width: 56, height: 56, borderRadius: 16,
              background: T.accChip,
              border: `1px solid rgba(15,81,50,0.25)`,
              color: T.acc,
            }}
          >
            <UploadIcon />
          </span>
          <p style={{ fontSize: 15.5, fontWeight: 600, letterSpacing: "-0.01em" }}>
            {drag ? "Drop it." : "Drop your CSV here"}
          </p>
          <p className="mt-1.5" style={{ fontSize: 12.5, color: T.ink2 }}>
            or click to choose a file from your computer
          </p>

          <div className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-full whitespace-nowrap max-w-full overflow-hidden" style={{ background: T.bgSoft, border: `1px solid ${T.hair}`, fontFamily: FM, fontSize: 10.5, color: T.ink3 }}>
            <span>100 rows</span>
            <span style={{ color: T.ink4 }}>·</span>
            <span>CSV / TXT / TSV</span>
            <span style={{ color: T.ink4 }}>·</span>
            <span>10 MB</span>
          </div>
        </div>

        {/* Subtle scan-line */}
        <span
          aria-hidden
          className="absolute inset-x-0 top-0 h-[2px] pointer-events-none"
          style={{
            background: `linear-gradient(90deg, transparent, ${T.acc}66, transparent)`,
            filter: "blur(1px)",
            opacity: drag ? 1 : 0,
            transition: "opacity 200ms ease",
          }}
        />
      </div>

      <div className="mt-6 flex flex-col items-center gap-3">
        <button
          type="button"
          data-testid="load-sample"
          onClick={(e) => { e.preventDefault(); onSample() }}
          className="group inline-flex items-center gap-2 cursor-pointer rounded-full px-3 py-1.5 transition-all"
          style={{
            fontFamily: FM, fontSize: 12, letterSpacing: "0.08em",
            textTransform: "uppercase", color: T.ink2, fontWeight: 600,
            background: T.accChip,
            border: `1px solid rgba(15,81,50,0.25)`,
            boxShadow: "0 1px 0 rgba(255,255,255,0.65) inset",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = T.ink
            e.currentTarget.style.transform = "translateY(-1px)"
            e.currentTarget.style.boxShadow = "0 1px 0 rgba(255,255,255,0.65) inset, 0 8px 18px -8px rgba(15,81,50,0.40)"
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = T.ink2
            e.currentTarget.style.transform = "translateY(0)"
            e.currentTarget.style.boxShadow = "0 1px 0 rgba(255,255,255,0.65) inset"
          }}
        >
          <span style={{ color: T.acc, fontWeight: 700 }}>↳</span>
          Or load a 20-row sample
          <svg
            width="11" height="11" viewBox="0 0 16 16" fill="none"
            className="transition-transform duration-200 group-hover:translate-x-0.5"
            style={{ color: T.acc }}
          >
            <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        {error && (
          <div
            className="mt-2 inline-flex items-center gap-2 px-3 py-2 rounded-lg"
            style={{
              background: T.badSoft, color: T.bad, border: `1px solid rgba(160,43,31,0.25)`,
              fontSize: 13,
            }}
          >
            <span style={{ width: 6, height: 6, background: T.bad, borderRadius: 999 }} />
            {error}
          </div>
        )}
      </div>

      <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-3 max-w-[820px] mx-auto">
        {[
          { t: "Privacy first",     d: "Your file is parsed in your browser. Nothing leaves your machine until you sign up." },
          { t: "Real validators",   d: "Same regex + curated lists we run in production for syntax, role, disposable, typo, TLD." },
          { t: "Audit-ready",       d: "Every rejection is sourced. Sign up to download the audit trail with the cleaned file." },
        ].map((x) => (
          <div
            key={x.t}
            className="rounded-xl p-4"
            style={{
              background: T.surface,
              border: `1px solid ${T.hair}`,
              boxShadow: `0 1px 0 rgba(255,255,255,0.6) inset`,
            }}
          >
            <p style={{ fontFamily: FM, fontSize: 10.5, letterSpacing: "0.16em", textTransform: "uppercase", color: T.ink3, fontWeight: 600 }}>
              <span style={{ color: T.acc }}>—</span> {x.t}
            </p>
            <p className="mt-2" style={{ fontSize: 13, color: T.ink2, lineHeight: 1.55 }}>
              {x.d}
            </p>
          </div>
        ))}
      </div>
    </section>
  )
}

function UploadIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <path d="M12 16V4M12 4l-5 5M12 4l5 5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  )
}

/* ───────────────────────────────────────────────────────────────────────────
   Preview budget gate

   Renders when an anonymous visitor has burned through the lifetime
   500-row preview budget on this device. Honest framing: we don't
   pretend the file is being processed and then bail — we tell them
   the preview cap is a thing, the signed-in tier picks up where it
   left off, and we move them to signup. Sample loads bypass the gate
   so people can still see the demo.
   ─────────────────────────────────────────────────────────────────────────── */

function PreviewBudgetGate() {
  // 500/500 is the always-true ratio at gate time, but rendering it
  // as a maxed-out meter is the whole storytelling beat — the user
  // SEES what they used. Generates the right "ah, makes sense" rather
  // than feeling cut off.
  const used = 500
  const cap  = 500

  const perks = [
    { t: "1,000 rows · monthly reset",         d: "Free tier picks up where the preview budget left off." },
    { t: "Live MX + the full 9 checks",        d: "Authoritative DNS, catch-all review, and row-level reasons." },
    { t: "Per-row audit trail · CSV download", d: "Every verdict sourced. Your team can see why each row was flagged." },
    { t: "Schema mapper · saved per workspace",d: "Map your CSV once, every future upload skips the column dance." },
  ]

  return (
    <section className="relative max-w-[860px] mx-auto">
      {/* Soft mint halo behind the card — same ambient feel as the
          rest of /try, just bigger so the card sits on a glow. */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(600px 280px at 50% 8%, ${T.accSoft}, transparent 70%)`,
          filter: "blur(2px)",
        }}
      />

      <div
        className="relative rounded-2xl overflow-hidden"
        style={{
          background: `linear-gradient(180deg, #FFFFFF 0%, ${T.surface2} 100%)`,
          border: `1px solid ${T.hair}`,
          boxShadow: `
            0 1px 0 rgba(255,255,255,0.65) inset,
            0 30px 80px -28px rgba(10,10,10,0.22),
            0 0 60px -28px rgba(15,81,50,0.20)
          `,
          animation: "try-rise 480ms ease-out both",
        }}
      >
        {/* Mint hairline at the top — Aperture-Light editorial cue */}
        <div
          aria-hidden
          className="absolute inset-x-12 top-0 h-px"
          style={{ background: `linear-gradient(90deg, transparent, ${T.acc}88, transparent)` }}
        />

        {/* HEADER */}
        <div className="px-6 sm:px-10 pt-8 sm:pt-10 pb-6 sm:pb-7">
          <div className="flex flex-col sm:flex-row sm:items-start gap-5 sm:gap-7">
            {/* Used meter — circular ring at 100% */}
            <div className="flex-shrink-0 relative mx-auto sm:mx-0" style={{ width: 96, height: 96 }}>
              <svg width={96} height={96} viewBox="0 0 96 96" className="overflow-visible">
                <defs>
                  <linearGradient id="gate-ring" x1="0" x2="1" y1="0" y2="1">
                    <stop offset="0%"  stopColor={T.accLite} />
                    <stop offset="100%" stopColor={T.acc} />
                  </linearGradient>
                </defs>
                <circle cx="48" cy="48" r="38" fill="none" stroke="rgba(10,10,10,0.06)" strokeWidth="8" />
                <circle
                  cx="48" cy="48" r="38" fill="none"
                  stroke="url(#gate-ring)"
                  strokeWidth="8" strokeLinecap="round"
                  strokeDasharray={2 * Math.PI * 38}
                  strokeDashoffset={0}
                  transform="rotate(-90 48 48)"
                  style={{ filter: `drop-shadow(0 0 8px ${T.acc}55)` }}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="tabular-nums leading-none" style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.03em", color: T.ink }}>
                  {used}
                </span>
                <span
                  className="mt-0.5"
                  style={{
                    fontFamily: FM, fontSize: 9.5, letterSpacing: "0.12em",
                    textTransform: "uppercase", color: T.ink3, fontWeight: 600,
                  }}
                >
                  /{cap}
                </span>
              </div>
            </div>

            <div className="flex-1 min-w-0 text-center sm:text-left">
              <p
                style={{
                  fontFamily: FM, fontSize: 10.5, letterSpacing: "0.18em",
                  textTransform: "uppercase", color: T.ink3, fontWeight: 600,
                }}
              >
                <span style={{ color: T.acc, marginRight: 8 }}>—</span>
                Preview budget · maxed
              </p>
              <h2
                className="mt-2.5 leading-[1.05] tracking-[-0.03em]"
                style={{ fontSize: "clamp(1.5rem, 4vw, 2.2rem)", fontWeight: 800, color: T.ink }}
              >
                You&rsquo;ve used your{" "}
                <em style={{ color: T.acc, fontStyle: "italic", fontWeight: 700 }}>500</em>{" "}
                preview rows.
              </h2>
              <p className="mt-3 max-w-[52ch] mx-auto sm:mx-0" style={{ fontSize: 13.5, color: T.ink2, lineHeight: 1.6 }}>
                Sign up free and you keep going — 1,000 fresh rows every month
                with the full pipeline behind a real account.
              </p>
            </div>
          </div>
        </div>

        {/* PERKS — what they actually get */}
        <div className="px-6 sm:px-10 pb-6 sm:pb-7">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3">
            {perks.map((p) => (
              <div
                key={p.t}
                className="rounded-xl p-3.5 sm:p-4"
                style={{
                  background: T.bgSoft,
                  border: `1px solid ${T.hair}`,
                }}
              >
                <div className="flex items-start gap-2.5">
                  <span
                    className="flex-shrink-0 inline-flex items-center justify-center mt-0.5"
                    style={{
                      width: 18, height: 18, borderRadius: 5,
                      background: T.accChip,
                      border: `1px solid rgba(15,81,50,0.30)`,
                      color: T.acc,
                    }}
                  >
                    <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
                      <path d="M3 8.5l3.5 3L13 5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                  <div className="min-w-0">
                    <p style={{ fontSize: 12.5, fontWeight: 700, color: T.ink, letterSpacing: "-0.005em", lineHeight: 1.35 }}>
                      {p.t}
                    </p>
                    <p className="mt-1" style={{ fontSize: 11.5, color: T.ink2, lineHeight: 1.5 }}>
                      {p.d}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* FOOTER — CTA strip in a different surface so it reads as
            the closer, not just another row */}
        <div
          className="px-6 sm:px-10 py-5 sm:py-6 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5"
          style={{
            borderTop: `1px solid ${T.hair}`,
            background: `linear-gradient(180deg, rgba(255,255,255,0.6) 0%, ${T.bgSoft} 100%)`,
          }}
        >
          <div className="flex-1 min-w-0">
            <p style={{ fontSize: 13, fontWeight: 700, color: T.ink, letterSpacing: "-0.005em" }}>
              No card. No commitment. 30 seconds to set up.
            </p>
            <p className="mt-0.5" style={{ fontSize: 11.5, color: T.ink3 }}>
              Google or email — your /try preview transfers over.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-2.5 flex-shrink-0">
            <Link
              href="/login"
              className="inline-flex items-center justify-center"
              style={{
                background: "transparent",
                color: T.ink2,
                padding: "10px 16px", borderRadius: 10,
                fontSize: 13, fontWeight: 600,
                border: `1px solid ${T.hair2}`,
              }}
            >
              Log in
            </Link>
            <Link
              href="/login?mode=signup"
              className="inline-flex items-center justify-center gap-1.5"
              style={{
                background: T.acc, color: "#FAF7EE",
                padding: "10px 18px", borderRadius: 10,
                fontSize: 13.5, fontWeight: 700,
                boxShadow: `0 16px 40px -14px ${T.accGlow}`,
              }}
            >
              Sign up free
              <ArrowR size={13} />
            </Link>
          </div>
        </div>
      </div>

      {/* Honesty footnote — outside the card so it reads as meta,
          not as part of the offer. Small, ink-3. */}
      <p className="mt-5 text-center" style={{ fontSize: 11, color: T.ink3, lineHeight: 1.6 }}>
        The 500-row preview cap stops a 1,000-row list from being split into ten
        drops and processed for free indefinitely. It&rsquo;s not a paywall — the
        free tier is genuinely free, just gated by signup.
      </p>
    </section>
  )
}

/* ───────────────────────────────────────────────────────────────────────────
   Processing
   ─────────────────────────────────────────────────────────────────────────── */

function Processing({
  progress,
  stage,
  stages,
  filename,
  filesize,
}: {
  progress: number
  stage: number
  stages: string[]
  filename: string
  filesize: number
}) {
  return (
    <section
      className="rounded-2xl p-5 sm:p-10 max-w-[860px] mx-auto"
      style={{
        background: T.surface,
        border: `1px solid ${T.hair}`,
        boxShadow: `0 1px 0 rgba(255,255,255,0.6) inset, 0 24px 60px -32px rgba(10,10,10,0.18)`,
      }}
    >
      <FileBar filename={filename} filesize={filesize} status="Processing" />

      <h2 className="mt-5 sm:mt-7 tracking-[-0.025em]" style={{ fontSize: "clamp(1.15rem, 3.6vw, 1.9rem)", fontWeight: 700 }}>
        Running your list through the pipeline.
      </h2>
      <p className="mt-1" style={{ fontSize: 12.5, color: T.ink2 }}>
        Eight validators per row · in your browser · no upload yet.
      </p>

      <div className="mt-8">
        <div
          className="h-1.5 w-full rounded-full overflow-hidden relative"
          style={{ background: T.bgSoft }}
        >
          <div
            className="absolute inset-y-0 left-0"
            style={{
              width: `${(progress * 100).toFixed(1)}%`,
              background: `linear-gradient(90deg, ${T.acc}, ${T.accDeep})`,
              transition: "width 80ms linear",
            }}
          />
        </div>
        <div className="mt-3 flex items-center justify-between">
          <span style={{ fontFamily: FM, fontSize: 12, color: T.ink2 }}>
            {stages[stage]}…
          </span>
          <span className="tabular-nums" style={{ fontFamily: FM, fontSize: 12, color: T.ink3 }}>
            {(progress * 100).toFixed(0)}%
          </span>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-2">
        {stages.slice(1, 9).map((s, i) => {
          const idx = i + 1
          const done = stage > idx
          const active = stage === idx
          return (
            <div
              key={s}
              className="rounded-lg px-3 py-2.5"
              style={{
                background: done ? T.accChip : active ? T.bgSoft : "transparent",
                border: `1px solid ${done ? "rgba(15,81,50,0.25)" : T.hair}`,
                transition: "all 200ms ease",
              }}
            >
              <div className="flex items-center gap-2">
                <span
                  className="inline-flex items-center justify-center"
                  style={{
                    width: 14, height: 14, borderRadius: 999,
                    background: done ? T.acc : active ? T.warn : "transparent",
                    border: `1px solid ${done ? T.acc : active ? T.warn : T.hair2}`,
                  }}
                >
                  {done && (
                    <svg width="8" height="8" viewBox="0 0 16 16" fill="none">
                      <path d="M3 8.5l3.5 3L13 5" stroke="#FAF7EE" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </span>
                <span style={{ fontSize: 11.5, color: done ? T.acc : active ? T.ink : T.ink3, fontWeight: 500 }}>
                  {s}
                </span>
              </div>
            </div>
          )
        })}
      </div>

      <p className="mt-6" style={{ fontSize: 12, color: T.ink3 }}>
        <span style={{ color: T.acc }}>*</span> Suppression and historical checks are
        approximate without a signed-in workspace. Sign up free to wire your
        bounce log + send history into the pipeline.
      </p>
    </section>
  )
}

/* ───────────────────────────────────────────────────────────────────────────
   File bar — header for processing/done states
   ─────────────────────────────────────────────────────────────────────────── */

function FileBar({ filename, filesize, status }: { filename: string; filesize: number; status: "Processing" | "Validated" }) {
  return (
    <div
      className="rounded-xl flex flex-wrap items-center gap-3 px-4 py-3"
      style={{ background: T.bgSoft, border: `1px solid ${T.hair}` }}
    >
      <span
        className="inline-flex items-center justify-center"
        style={{
          width: 32, height: 32, borderRadius: 8,
          background: T.accChip,
          border: `1px solid rgba(15,81,50,0.25)`,
          color: T.acc,
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
          <path d="M14 3v6h6" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
          <path d="M8 14h8M8 17h5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        </svg>
      </span>
      <div className="leading-tight min-w-0">
        <p style={{ fontSize: 13.5, fontWeight: 500, color: T.ink, letterSpacing: "-0.005em" }}>{filename}</p>
        <p className="mt-0.5" style={{ fontFamily: FM, fontSize: 11, color: T.ink3 }}>
          {(filesize / 1024).toFixed(1)} KB · uploaded just now
        </p>
      </div>
      <div className="ml-auto flex items-center gap-2">
        <span
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full"
          style={{
            background: status === "Validated" ? T.accChip : T.warnSoft,
            border: `1px solid ${status === "Validated" ? "rgba(15,81,50,0.25)" : "rgba(184,92,0,0.25)"}`,
            color: status === "Validated" ? T.acc : T.warn,
            fontFamily: FM, fontSize: 10.5, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase",
          }}
        >
          <span
            style={{
              width: 5, height: 5, borderRadius: 999,
              background: status === "Validated" ? T.acc : T.warn,
              animation: status === "Processing" ? "try-pulse 1.2s infinite" : undefined,
            }}
          />
          {status}
        </span>
      </div>
    </div>
  )
}

/* ───────────────────────────────────────────────────────────────────────────
   Result view — score + KPI strip + tabs
   ─────────────────────────────────────────────────────────────────────────── */

function ResultView({
  filename,
  filesize,
  rows,
  tab,
  setTab,
  onAnother,
}: {
  filename: string
  filesize: number
  rows: Row[]
  tab: Tab
  setTab: (t: Tab) => void
  onAnother: () => void
}) {
  const counts = useMemo(() => {
    const c: Record<Verdict, number> = { deliverable: 0, role: 0, disposable: 0, typo: 0, syntax: 0, tld: 0 }
    rows.forEach((r) => { c[r.verdict] += 1 })
    return c
  }, [rows])

  const total = rows.length
  const ok = counts.deliverable
  const risky = counts.role + counts.typo + counts.tld
  const bad = counts.disposable + counts.syntax
  const score = total > 0 ? (ok / total) * 100 : 0

  return (
    <>
      <FileBar filename={filename} filesize={filesize} status="Validated" />

      <ResultHeader score={score} total={total} ok={ok} risky={risky} bad={bad} onAnother={onAnother} />

      <Tabs value={tab} onChange={setTab} />

      <div className="mt-6">
        {tab === "overview"   && <OverviewTab counts={counts} total={total} />}
        {tab === "rows"       && <RowsTab rows={rows} isSample={filename === "sample-q2-prospects.csv"} />}
        {tab === "validators" && <ValidatorsTab counts={counts} total={total} />}
        {tab === "mapper"     && <MapperTab />}
        {tab === "compare"    && <CompareTab />}
      </div>
    </>
  )
}

function ResultHeader({
  score, total, ok, risky, bad, onAnother,
}: {
  score: number; total: number; ok: number; risky: number; bad: number; onAnother: () => void
}) {
  return (
    <section
      data-testid="try-result"
      className="mt-6 rounded-2xl overflow-hidden"
      style={{
        background: T.surface,
        border: `1px solid ${T.hair}`,
        boxShadow: `0 1px 0 rgba(255,255,255,0.6) inset, 0 24px 60px -32px rgba(10,10,10,0.18)`,
        animation: "try-rise 480ms ease-out both",
      }}
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
        <div className="lg:col-span-5 p-5 sm:p-9 flex items-center gap-4 sm:gap-7 border-b lg:border-b-0 lg:border-r" style={{ borderColor: T.hair }}>
          <ScoreRing score={score} />
          <div className="min-w-0">
            <p style={{ fontFamily: FM, fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: T.ink3 }}>
              Deliverability score
            </p>
            <p
              data-testid="try-score"
              className="mt-1.5 tabular-nums"
              style={{ fontSize: "clamp(1.9rem, 5vw, 3.4rem)", fontWeight: 800, letterSpacing: "-0.04em" }}
            >
              {score.toFixed(0)}<span style={{ color: T.acc, fontWeight: 700 }}>%</span>
            </p>
            <p className="mt-1" style={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.5, maxWidth: "30ch" }}>
              {score >= 90
                ? "Strong list. Safe to send."
                : score >= 75
                ? "Good list — drop the risky rows before send."
                : "Risky list. Cleaning is non-optional."}
            </p>
          </div>
        </div>

        <div className="lg:col-span-7 grid grid-cols-3">
          <Kpi label="Processed"   value={total}      hue={T.blue} />
          <Kpi label="Deliverable" value={ok}         hue={T.acc}  divider />
          <Kpi label="Removed"     value={risky + bad} hue={T.bad}  divider />
        </div>
      </div>

      <button
        onClick={onAnother}
        className="absolute right-4 top-4 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md transition-colors hover:opacity-80"
        style={{
          fontFamily: FM, fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase",
          color: T.ink3, background: T.bgSoft, border: `1px solid ${T.hair}`,
          position: "relative", display: "none",
        }}
      >
        try another file
      </button>
    </section>
  )
}

function ScoreRing({ score }: { score: number }) {
  const R = 52
  const C = 2 * Math.PI * R
  const offset = C - (score / 100) * C
  return (
    <div className="relative shrink-0 w-[88px] h-[88px] sm:w-[128px] sm:h-[128px]">
      <svg className="w-full h-full" viewBox="0 0 128 128">
        <defs>
          <linearGradient id="try-ring" x1="0" x2="1" y1="0" y2="1">
            <stop offset="0%"  stopColor={T.accLite} />
            <stop offset="100%" stopColor={T.acc} />
          </linearGradient>
        </defs>
        <circle cx="64" cy="64" r={R} fill="none" stroke="rgba(10,10,10,0.06)" strokeWidth="10" />
        <circle
          cx="64" cy="64" r={R} fill="none"
          stroke="url(#try-ring)"
          strokeWidth="10" strokeLinecap="round"
          strokeDasharray={C}
          strokeDashoffset={offset}
          transform="rotate(-90 64 64)"
          style={{ transition: "stroke-dashoffset 800ms cubic-bezier(.2,.8,.2,1)" }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <svg className="w-6 h-6 sm:w-[34px] sm:h-[34px]" viewBox="0 0 24 24" fill="none">
          <path d="M5 12.5l4.5 4L19 7.5" stroke={T.acc} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    </div>
  )
}

function Kpi({ label, value, hue, divider }: { label: string; value: number; hue: string; divider?: boolean }) {
  const display = useCountUp(value, 900)
  return (
    <div className="p-3 sm:p-7 relative min-w-0" style={{ borderLeft: divider ? `1px solid ${T.hair}` : undefined }}>
      <div className="flex items-center gap-1.5 min-w-0">
        <span className="flex-shrink-0" style={{ width: 6, height: 6, borderRadius: 999, background: hue, boxShadow: `0 0 8px ${hue}55` }} />
        <span className="truncate min-w-0" style={{ fontFamily: FM, fontSize: 9.5, letterSpacing: "0.08em", textTransform: "uppercase", color: T.ink3, fontWeight: 600 }}>
          {label}
        </span>
      </div>
      <p className="mt-1.5 sm:mt-3 tabular-nums" style={{ fontSize: "clamp(1.3rem, 3.4vw, 2.2rem)", fontWeight: 800, letterSpacing: "-0.035em" }}>
        {display.toLocaleString("en-US")}
      </p>
    </div>
  )
}

/* ───────────────────────────────────────────────────────────────────────────
   Tabs
   ─────────────────────────────────────────────────────────────────────────── */

function Tabs({ value, onChange }: { value: Tab; onChange: (t: Tab) => void }) {
  // Mapper + Compare started as hard-coded mock tables ("q1-prospects
  // .csv", fake confidence scores) that overflowed on mobile and read
  // as theatre. Rewritten as feature-explainer panels: plain copy +
  // small visual cues describing what each feature does once signed
  // in, no fake data. Stays informational, looks clean on phones.
  const items: { k: Tab; l: string; locked?: boolean }[] = [
    { k: "overview",   l: "Overview"   },
    { k: "rows",       l: "Rows"       },
    { k: "validators", l: "Validators" },
    { k: "mapper",     l: "Mapper",     locked: true },
    { k: "compare",    l: "Compare",    locked: true },
  ]
  return (
    <div
      className="mt-6 sm:mt-8 flex flex-nowrap sm:flex-wrap gap-1 p-1 rounded-xl overflow-x-auto -mx-1 px-1"
      style={{ background: T.surface, border: `1px solid ${T.hair}`, width: "fit-content", maxWidth: "100%" }}
    >
      {items.map((it) => {
        const active = value === it.k
        return (
          <button
            key={it.k}
            onClick={() => onChange(it.k)}
            className="inline-flex items-center gap-2 transition-all whitespace-nowrap"
            style={{
              padding: "8px 14px",
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 500,
              color: active ? T.ink : T.ink2,
              background: active ? T.accChip : "transparent",
              boxShadow: active ? "inset 0 0 0 1px rgba(15,81,50,0.30)" : "none",
              cursor: "pointer",
            }}
          >
            {it.l}
            {it.locked && <Lock size={11} color={active ? T.acc : T.ink3} />}
          </button>
        )
      })}
    </div>
  )
}

function Lock({ size = 12, color = "currentColor" }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <rect x="3" y="7" width="10" height="7" rx="1.5" stroke={color} strokeWidth="1.4" />
      <path d="M5 7V5a3 3 0 0 1 6 0v2" stroke={color} strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  )
}

/* ───────────────────────────────────────────────────────────────────────────
   Tabs — content
   ─────────────────────────────────────────────────────────────────────────── */

function OverviewTab({ counts, total }: { counts: Record<Verdict, number>; total: number }) {
  const segs: { k: Verdict; v: number }[] = (Object.keys(counts) as Verdict[]).map((k) => ({ k, v: counts[k] }))
  // Two-way hover sync between the donut + the breakdown list. Hovering
  // a list item dims the other donut slices + brightens the matching
  // one, and vice versa. Center number swaps from total → segment count
  // while a slice is active.
  const [hovered, setHovered] = useState<Verdict | null>(null)
  const hoveredSeg = hovered ? segs.find((s) => s.k === hovered) : null
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
      <Panel title="Verdict breakdown" subtitle="Where every row ended up." span="lg:col-span-7">
        <div className="flex flex-col sm:flex-row sm:flex-wrap items-center sm:items-center gap-5 sm:gap-7">
          <Donut
            segs={segs}
            total={total}
            hovered={hovered}
            onHover={setHovered}
            centerValue={hoveredSeg ? hoveredSeg.v : total}
            centerLabel={hoveredSeg ? VERDICT_META[hoveredSeg.k].label : "total rows"}
          />
          <div className="w-full sm:flex-1 sm:min-w-[220px]">
            <ul className="flex flex-col gap-1">
              {segs.map((s) => {
                const m = VERDICT_META[s.k]
                const pct = total > 0 ? (s.v / total) * 100 : 0
                const isActive = hovered === s.k
                const dimmed = hovered !== null && !isActive
                return (
                  <li
                    key={s.k}
                    onMouseEnter={() => setHovered(s.k)}
                    onMouseLeave={() => setHovered(null)}
                    className="flex items-center justify-between gap-3 px-2 py-1.5 rounded-lg cursor-pointer transition-all"
                    style={{
                      background: isActive ? T.accChip : "transparent",
                      opacity: dimmed ? 0.45 : 1,
                    }}
                  >
                    <span className="inline-flex items-center gap-2 min-w-0">
                      <span
                        className="flex-shrink-0 transition-transform"
                        style={{
                          width: 8, height: 8, borderRadius: 999,
                          background: m.color,
                          boxShadow: `0 0 6px ${m.color}${isActive ? "AA" : "55"}`,
                          transform: isActive ? "scale(1.35)" : "scale(1)",
                        }}
                      />
                      <span className="truncate" style={{ fontSize: 13, color: isActive ? T.ink : T.ink2, fontWeight: isActive ? 600 : 400 }}>
                        {m.label}
                      </span>
                    </span>
                    <span className="inline-flex items-center gap-2.5 flex-shrink-0">
                      <span className="tabular-nums" style={{ fontFamily: FM, fontSize: 11.5, color: T.ink3, minWidth: 34, textAlign: "right" }}>
                        {pct.toFixed(0)}%
                      </span>
                      <span className="tabular-nums" style={{ fontSize: 13, fontWeight: 600, color: T.ink, minWidth: 24, textAlign: "right" }}>
                        {s.v}
                      </span>
                    </span>
                  </li>
                )
              })}
            </ul>
          </div>
        </div>
      </Panel>

      <Panel title="What you'd save" subtitle="Removing bad rows before send." span="lg:col-span-5">
        <div className="grid grid-cols-2 gap-2.5 sm:gap-4">
          {[
            { l: "Wasted sends avoided", v: `${counts.disposable + counts.syntax + counts.role + counts.typo + counts.tld}`, sub: "rows × $0.005+" },
            { l: "Bounce rate impact", v: `−${total > 0 ? (((counts.disposable + counts.syntax) / total) * 100).toFixed(1) : "0.0"}%`, sub: "back under ESP threshold" },
            { l: "Sender-score risk", v: counts.role + counts.typo > 5 ? "High" : "Low", sub: "role + typo exposure" },
            { l: "Time saved", v: "~3.2s", sub: "vs. 25 min by hand" },
          ].map((x) => (
            <div key={x.l} className="rounded-xl p-3 sm:p-4 min-w-0" style={{ background: T.bgSoft, border: `1px solid ${T.hair}` }}>
              <p className="truncate" style={{ fontFamily: FM, fontSize: 9.5, letterSpacing: "0.10em", textTransform: "uppercase", color: T.ink3, fontWeight: 600 }}>
                {x.l}
              </p>
              <p className="mt-1.5 tabular-nums" style={{ fontSize: "clamp(1.05rem, 3.6vw, 1.4rem)", fontWeight: 800, letterSpacing: "-0.03em" }}>
                {x.v}
              </p>
              <p className="mt-1 truncate" style={{ fontSize: 11, color: T.ink3 }}>{x.sub}</p>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  )
}

function Donut({
  segs,
  total,
  hovered,
  onHover,
  centerValue,
  centerLabel,
}: {
  segs: { k: Verdict; v: number }[]
  total: number
  hovered?: Verdict | null
  onHover?: (v: Verdict | null) => void
  centerValue?: number
  centerLabel?: string
}) {
  const R = 64
  const SW = 18
  const SW_ACTIVE = 22
  const C = 2 * Math.PI * R
  let acc = 0
  const display = centerValue ?? total
  const label   = centerLabel ?? "total rows"
  return (
    <div className="relative shrink-0 w-[140px] h-[140px] sm:w-[168px] sm:h-[168px]">
      <svg className="w-full h-full overflow-visible" viewBox="0 0 168 168">
        <circle cx="84" cy="84" r={R} stroke="rgba(10,10,10,0.06)" strokeWidth={SW} fill="none" />
        {segs.map((s) => {
          const m = VERDICT_META[s.k]
          const len = total > 0 ? (s.v / total) * C : 0
          const dash = `${len} ${C - len}`
          const offset = -acc
          acc += len
          const isActive = hovered === s.k
          const dimmed   = hovered !== null && hovered !== undefined && !isActive
          return (
            <circle
              key={s.k}
              cx="84" cy="84" r={R}
              stroke={m.color}
              strokeWidth={isActive ? SW_ACTIVE : SW}
              fill="none"
              strokeDasharray={dash}
              strokeDashoffset={offset}
              transform="rotate(-90 84 84)"
              opacity={dimmed ? 0.30 : isActive ? 1 : 0.92}
              style={{
                transition: "opacity 160ms ease, stroke-width 160ms ease",
                cursor: onHover ? "pointer" : undefined,
                pointerEvents: s.v > 0 ? "stroke" : "none",
                filter: isActive ? `drop-shadow(0 0 8px ${m.color}88)` : undefined,
              }}
              onMouseEnter={onHover ? () => onHover(s.k) : undefined}
              onMouseLeave={onHover ? () => onHover(null) : undefined}
            />
          )
        })}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        <p className="tabular-nums transition-all" style={{ fontSize: "clamp(1.4rem, 5vw, 1.75rem)", fontWeight: 800, letterSpacing: "-0.035em" }}>
          {display}
        </p>
        <p className="transition-colors" style={{ fontFamily: FM, fontSize: 9.5, letterSpacing: "0.12em", textTransform: "uppercase", color: hovered ? VERDICT_META[hovered].color : T.ink3, fontWeight: 600 }}>
          {label}
        </p>
      </div>
    </div>
  )
}

/* ───────────────────────────────────────────────────────────────────────────
   Rows tab
   ─────────────────────────────────────────────────────────────────────────── */

function RowsTab({ rows, isSample }: { rows: Row[]; isSample: boolean }) {
  // Same logged-in awareness as StickyCta — see comment there. The
  // locked-rows overlay below also has to flip its nudge from
  // "Sign up" to "Open dashboard" when the visitor already has an
  // account, otherwise we're bouncing them to /login for nothing.
  const { user } = useAuth()
  const isLoggedIn = !!user
  const [filter, setFilter] = useState<Verdict | "all">("all")
  const visible = filter === "all" ? rows : rows.filter((r) => r.verdict === filter)
  const previewN = 20
  const preview = visible.slice(0, previewN)
  const lockedCount = Math.max(0, visible.length - preview.length)

  const filters: { k: Verdict | "all"; l: string }[] = [
    { k: "all",         l: "All" },
    { k: "deliverable", l: "Deliverable" },
    { k: "role",        l: "Role-based"  },
    { k: "disposable",  l: "Disposable"  },
    { k: "typo",        l: "Typo"        },
    { k: "tld",         l: "Risky TLD"   },
    { k: "syntax",      l: "Bad syntax"  },
  ]

  return (
    <Panel
      title="Per-row results"
      subtitle={
        visible.length === 0
          ? filter === "all"
            ? "No rows detected in this file."
            : `No ${filters.find((f) => f.k === filter)?.l.toLowerCase() ?? "matching"} rows in this list.`
          : `Showing ${preview.length} of ${visible.length}. Sign up to view + export the full set.`
      }
    >
      {/* Filter row — horizontal scroll on mobile (no wrap into 3 messy
          lines), wraps cleanly on sm+. Negative margins let it bleed to
          the panel edges so the scroll affordance is obvious. */}
      <div
        className="flex sm:flex-wrap items-center gap-1.5 mb-4 sm:mb-5 -mx-4 px-4 sm:mx-0 sm:px-0 overflow-x-auto sm:overflow-visible [&::-webkit-scrollbar]:hidden"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        {filters.map((f) => {
          const active = filter === f.k
          return (
            <button
              key={f.k}
              onClick={() => setFilter(f.k)}
              className="inline-flex items-center whitespace-nowrap flex-shrink-0 transition-all"
              style={{
                padding: "5px 11px", borderRadius: 999,
                fontSize: 11.5, fontWeight: 500,
                color: active ? T.acc : T.ink2,
                background: active ? T.accChip : T.bgSoft,
                border: `1px solid ${active ? "rgba(15,81,50,0.30)" : T.hair}`,
                cursor: "pointer",
              }}
            >
              {f.l}
            </button>
          )
        })}
      </div>

      <div className="rounded-xl overflow-hidden relative" style={{ background: T.bgSoft, border: `1px solid ${T.hair}` }}>
        {/* Header row — desktop only. On mobile each row is a stacked
            card (email on top, verdict pill + reason below), so column
            headers would be misleading. */}
        <div
          className="hidden sm:grid items-center px-4 py-2.5"
          style={{
            gridTemplateColumns: "minmax(0,2fr) minmax(120px,1fr) minmax(0,2fr)",
            borderBottom: `1px solid ${T.hair}`,
            background: T.bgSoft,
            fontFamily: FM, fontSize: 10.5, letterSpacing: "0.14em", textTransform: "uppercase", color: T.ink3,
          }}
        >
          <span>Email</span>
          <span>Verdict</span>
          <span>Reason</span>
        </div>

        {preview.length === 0 && (
          // Empty-state slot — used when the active filter has zero
          // matches (or the file is empty). Lives inside the same
          // bordered box as the rows so the table doesn't visually
          // collapse to just the header strip + a gap.
          <div className="flex flex-col items-center justify-center text-center px-6 py-10" style={{ background: T.surface }}>
            <span
              aria-hidden
              className="inline-flex items-center justify-center mb-3"
              style={{
                width: 36, height: 36, borderRadius: 999,
                background: T.bgSoft, border: `1px solid ${T.hair}`, color: T.ink3,
              }}
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.4" />
                <path d="M11 11l3 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
              </svg>
            </span>
            <p style={{ fontSize: 13, fontWeight: 600, color: T.ink, letterSpacing: "-0.005em" }}>
              {filter === "all" ? "No rows to show" : `No ${filters.find((f) => f.k === filter)?.l.toLowerCase()} rows`}
            </p>
            <p className="mt-1" style={{ fontSize: 12, color: T.ink2 }}>
              {filter === "all"
                ? "Upload a CSV with at least one row to see results here."
                : "Try a different filter to see the rows that were flagged."}
            </p>
          </div>
        )}

        {preview.map((r, i) => {
          const m = VERDICT_META[r.verdict]
          const VerdictPill = (
            <span
              className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded flex-shrink-0"
              style={{
                background: m.soft, color: m.color, border: `1px solid ${m.color}33`,
                fontFamily: FM, fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
              }}
            >
              <span style={{ width: 5, height: 5, borderRadius: 999, background: m.color }} />
              {m.label}
            </span>
          )
          return (
            <div
              key={i}
              className="px-4 py-3 sm:py-2.5 sm:grid sm:items-center"
              style={{
                gridTemplateColumns: "minmax(0,2fr) minmax(120px,1fr) minmax(0,2fr)",
                borderBottom: i < preview.length - 1 ? `1px solid ${T.hair}` : undefined,
                background: T.surface,
              }}
            >
              {/* Mobile: stacked card */}
              <div className="sm:hidden">
                <div className="flex items-center justify-between gap-2 min-w-0">
                  <span className="truncate min-w-0" style={{ fontFamily: FM, fontSize: 12.5, color: T.ink }}>
                    {r.raw}
                  </span>
                  {VerdictPill}
                </div>
                <p className="mt-1" style={{ fontSize: 11.5, color: T.ink2, lineHeight: 1.45 }}>
                  {r.reason}
                </p>
              </div>

              {/* Desktop: 3-col grid */}
              <span className="hidden sm:block truncate" style={{ fontFamily: FM, fontSize: 12.5, color: T.ink }}>
                {r.raw}
              </span>
              <span className="hidden sm:block">{VerdictPill}</span>
              <span className="hidden sm:block truncate" style={{ fontSize: 12, color: T.ink2 }}>{r.reason}</span>
            </div>
          )
        })}

        {lockedCount > 0 && (
          <div className="relative">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="px-4 py-3 sm:py-2.5 sm:grid sm:items-center"
                style={{
                  gridTemplateColumns: "minmax(0,2fr) minmax(120px,1fr) minmax(0,2fr)",
                  borderTop: `1px solid ${T.hair}`,
                  background: T.surface,
                  filter: `blur(${2 + i * 1.4}px)`,
                  opacity: 1 - i * 0.18,
                }}
              >
                <span className="truncate" style={{ fontFamily: FM, fontSize: 12.5, color: T.ink2 }}>locked@example.com</span>
                <span className="hidden sm:block" style={{ fontSize: 12.5, color: T.ink3 }}>—</span>
                <span className="hidden sm:block" style={{ fontSize: 12, color: T.ink3 }}>Sign up to reveal</span>
              </div>
            ))}
            <div
              className="absolute inset-0 flex items-center justify-center"
              style={{
                background: `linear-gradient(180deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.95) 60%)`,
              }}
            >
              <div className="flex flex-col items-center text-center px-6">
                <span
                  className="inline-flex items-center justify-center"
                  style={{
                    width: 36, height: 36, borderRadius: 999,
                    background: T.accChip, border: `1px solid rgba(15,81,50,0.25)`, color: T.acc,
                  }}
                >
                  <Lock size={14} color={T.acc} />
                </span>
                <p className="mt-3" style={{ fontSize: 14, fontWeight: 600, color: T.ink, letterSpacing: "-0.01em" }}>
                  {lockedCount} more rows + export
                </p>
                <p className="mt-1" style={{ fontSize: 12.5, color: T.ink2 }}>
                  {isLoggedIn
                    ? "Run this list inside the dashboard to see every row and export."
                    : "Free signup unlocks all rows and CSV / JSON / Parquet download."}
                </p>
                <Link
                  href={isLoggedIn ? "/dashboard" : "/login?mode=signup"}
                  className="mt-4 inline-flex items-center gap-1.5"
                  style={{
                    background: T.acc, color: "#FAF7EE",
                    padding: "8px 14px", borderRadius: 8,
                    fontSize: 12.5, fontWeight: 600,
                  }}
                >
                  {isLoggedIn
                    ? "Open dashboard"
                    : (isSample ? "Sign up — clean my list" : "Sign up — keep this report")}
                  <ArrowR />
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </Panel>
  )
}

/* ───────────────────────────────────────────────────────────────────────────
   Validators tab
   ─────────────────────────────────────────────────────────────────────────── */

function ValidatorsTab({ counts, total }: { counts: Record<Verdict, number>; total: number }) {
  const checks = [
    { name: "Syntax",     tag: "01", caught: counts.syntax,     desc: "RFC parser + homograph guard." },
    { name: "Live MX*",   tag: "02", caught: 0,                 desc: "Authoritative DNS — approximate without signed-in workspace." },
    { name: "Disposable", tag: "03", caught: counts.disposable, desc: "9,200+ throwaway providers." },
    { name: "Role-based", tag: "04", caught: counts.role,       desc: "info@ / support@ / no-reply@ etc." },
    { name: "Typo guard", tag: "05", caught: counts.typo,       desc: "Levenshtein vs. top providers." },
    { name: "TLD trust",  tag: "06", caught: counts.tld,        desc: "Suspicious country codes weighted." },
    { name: "Suppression*", tag: "07", caught: 0,               desc: "Cross-checked against your bounce log." },
    { name: "Historical*", tag: "08", caught: 0,                desc: "Per-address deliverability history." },
  ]

  return (
    <Panel title="Per-validator breakdown" subtitle="What each check caught on your list.">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {checks.map((c) => {
          const passed = total - c.caught
          const passedPct = total > 0 ? (passed / total) * 100 : 0
          const caughtPct = total > 0 ? (c.caught / total) * 100 : 0
          return (
            <div
              key={c.name}
              className="rounded-xl p-5"
              style={{ background: T.surface2, border: `1px solid ${T.hair}` }}
            >
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-2">
                  <span style={{ fontFamily: FM, fontSize: 10.5, color: T.ink3, letterSpacing: "0.14em" }}>/{c.tag}</span>
                  <span style={{ fontSize: 14, fontWeight: 600, letterSpacing: "-0.01em", color: T.ink }}>{c.name}</span>
                </span>
                <span style={{ fontFamily: FM, fontSize: 11, fontWeight: 600, color: c.caught > 0 ? T.warn : T.acc }}>
                  {c.caught > 0 ? `${c.caught} caught` : "All passed"}
                </span>
              </div>
              <p className="mt-2" style={{ fontSize: 12.5, color: T.ink2 }}>{c.desc}</p>
              <div className="mt-4 h-1.5 rounded-full overflow-hidden flex" style={{ background: "rgba(10,10,10,0.05)" }}>
                <div style={{ width: `${passedPct}%`, background: T.acc }} />
                <div style={{ width: `${caughtPct}%`, background: c.name === "Syntax" || c.name === "Disposable" ? T.bad : T.warn }} />
              </div>
              <div className="mt-2 flex items-center justify-between" style={{ fontFamily: FM, fontSize: 10.5, color: T.ink3 }}>
                <span>{passed} passed</span>
                <span>{c.caught} caught</span>
              </div>
            </div>
          )
        })}
      </div>
      <p className="mt-5" style={{ fontSize: 12, color: T.ink3 }}>
        <span style={{ color: T.acc }}>*</span> Approximate without a signed-in workspace.
        Sign up to enable live MX, suppression, and per-tenant history.
      </p>
    </Panel>
  )
}

/* ───────────────────────────────────────────────────────────────────────────
   Mapper + Compare — feature explainers (locked behind signup)

   Previous iteration tried to teaser these with mock tables (fake
   schema confidence rows, fake `q1-prospects.csv` diff). They broke
   on mobile and read as theatre to anyone paying attention. Rewritten
   as plain copy + small visual cues describing what each feature does.
   No fake data, mobile-first stacked layout.
   ─────────────────────────────────────────────────────────────────────────── */

function MapperTab() {
  return (
    <Panel
      title="Schema mapper"
      subtitle="Save the column mapping for your CSV shape — next list with the same headers runs instantly, no re-mapping."
    >
      <div className="flex flex-col gap-3">
        <ExplainerRow
          tag="01"
          title="Auto-detects your columns"
          body="DataBridge inspects your headers + sample values and proposes a mapping to the canonical schema (email, name, company, segment, joined date, etc.). You confirm, override, or rename."
        />
        <ExplainerRow
          tag="02"
          title="Saves the mapping per workspace"
          body="Once saved, every future upload with the same shape skips the mapping step entirely. Switch between mappings per list when you have more than one source CSV format."
        />
        <ExplainerRow
          tag="03"
          title="ESP-aware export"
          body="On download, the cleaned CSV can be re-shaped to match Klaviyo / Mailchimp / HubSpot / Customer.io column conventions. One mapping, every destination."
        />
      </div>

      <LockedAction
        title="Save your CSV's schema"
        body="Stop re-mapping the same columns every Monday."
        cta="Sign up to save mapping"
      />
    </Panel>
  )
}

function CompareTab() {
  return (
    <Panel
      title="Compare two lists"
      subtitle="Upload last quarter's list + this quarter's list and see exactly what moved — no spreadsheet gymnastics."
    >
      <div className="flex flex-col gap-3">
        <ExplainerRow
          tag="01"
          title="Net new"
          dotColor={T.acc}
          body="Addresses that appear in the new list but not the old one — your real growth, separated from list churn."
        />
        <ExplainerRow
          tag="02"
          title="Churned"
          dotColor={T.bad}
          body="Addresses present last time but missing now. Useful for win-back segments or feeding back into your CRM as unsubscribes."
        />
        <ExplainerRow
          tag="03"
          title="Re-engaged"
          dotColor={T.warn}
          body="Addresses that were flagged risky last run but cleared this run — historic bouncers that came back. Don't waste them by quietly suppressing."
        />
        <ExplainerRow
          tag="04"
          title="In both"
          dotColor={T.blue}
          body="The stable core. Compare deliverability scores between runs to spot domains starting to degrade before bounces spike."
        />
      </div>

      <LockedAction
        title="Run a diff against a previous list"
        body="Upload two CSVs in the dashboard — the diff lands in seconds."
        cta="Sign up to compare"
      />
    </Panel>
  )
}

/** Tagged explainer row. Numbered chip on the left (or a colored dot
 *  when a `dotColor` is passed — used by CompareTab to tie each row to
 *  its semantic color), title + body on the right. Stacks comfortably
 *  on every viewport: no fixed grids, no overflow. */
function ExplainerRow({
  tag, title, body, dotColor,
}: { tag: string; title: string; body: string; dotColor?: string }) {
  return (
    <div
      className="rounded-xl p-3.5 sm:p-4 flex gap-3 sm:gap-4"
      style={{ background: T.bgSoft, border: `1px solid ${T.hair}` }}
    >
      <span
        className="flex-shrink-0 inline-flex items-center justify-center"
        style={{
          width: 32, height: 32, borderRadius: 8,
          background: dotColor ? `${dotColor}1A` : T.accChip,
          border: `1px solid ${dotColor ? `${dotColor}44` : "rgba(15,81,50,0.25)"}`,
          color: dotColor ?? T.acc,
          fontFamily: FM, fontSize: 11, fontWeight: 700, letterSpacing: "0.04em",
        }}
      >
        {tag}
      </span>
      <div className="min-w-0 flex-1">
        <p style={{ fontSize: 13.5, fontWeight: 600, color: T.ink, letterSpacing: "-0.005em" }}>
          {title}
        </p>
        <p className="mt-1" style={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.55 }}>
          {body}
        </p>
      </div>
    </div>
  )
}

// SecurityTab removed pre-launch. It carried a "SOC 2 Type II — Annual
// audit, controls reviewed quarterly" claim we don't actually hold,
// alongside hard-coded fake timestamps ("+0.4s file parsed…") that
// read as theatre instead of telemetry. Both are exactly what an HN
// dev fact-checks first. Real trust signals (DPA, TLS, retention)
// already live on /privacy and /dpa; we'll route Security back here
// once we have a SOC 2 report to link to. Tracked in
// docs/post-launch-backlog.md as "Trust page".

/* ───────────────────────────────────────────────────────────────────────────
   Shared bits
   ─────────────────────────────────────────────────────────────────────────── */

function Panel({ title, subtitle, children, span = "" }: { title: string; subtitle?: string; children: React.ReactNode; span?: string }) {
  return (
    <section
      className={`rounded-2xl p-4 sm:p-7 ${span}`}
      style={{
        background: T.surface,
        border: `1px solid ${T.hair}`,
        boxShadow: `0 1px 0 rgba(255,255,255,0.6) inset`,
        animation: "try-rise 360ms ease-out both",
      }}
    >
      <div className="flex items-baseline justify-between gap-4">
        <div>
          <h3 className="tracking-[-0.015em]" style={{ fontSize: 16, fontWeight: 700 }}>{title}</h3>
          {subtitle && <p className="mt-1" style={{ fontSize: 12.5, color: T.ink2 }}>{subtitle}</p>}
        </div>
      </div>
      <div className="mt-5">{children}</div>
    </section>
  )
}

function LockedAction({ title, body, cta }: { title: string; body: string; cta: string }) {
  // Same logged-in flip — anyone already signed in shouldn't see
  // "Sign up to …", we send them to the dashboard instead. Copy of
  // the CTA itself is generic ("Open dashboard") so it works for
  // mapper / compare / audit equally without per-call overrides.
  const { user } = useAuth()
  const isLoggedIn = !!user

  return (
    <div
      className="mt-5 rounded-xl p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center sm:gap-4 gap-3 sm:justify-between"
      style={{
        background: `linear-gradient(180deg, rgba(15,81,50,0.06), rgba(15,81,50,0.01))`,
        border: `1px solid rgba(15,81,50,0.20)`,
      }}
    >
      <div className="flex items-start sm:items-center gap-3 min-w-0">
        <span
          className="flex-shrink-0 inline-flex items-center justify-center"
          style={{
            width: 32, height: 32, borderRadius: 8,
            background: T.accChip, border: `1px solid rgba(15,81,50,0.30)`, color: T.acc,
          }}
        >
          <Lock size={14} color={T.acc} />
        </span>
        <div className="min-w-0">
          <p style={{ fontSize: 13.5, fontWeight: 700, letterSpacing: "-0.005em" }}>{title}</p>
          <p style={{ fontSize: 12.5, color: T.ink2, marginTop: 2, lineHeight: 1.5 }}>{body}</p>
        </div>
      </div>
      <Link
        href={isLoggedIn ? "/dashboard" : "/login?mode=signup"}
        className="inline-flex items-center justify-center gap-1.5 w-full sm:w-auto flex-shrink-0"
        style={{
          background: T.acc, color: "#FAF7EE",
          padding: "9px 14px", borderRadius: 8,
          fontSize: 13, fontWeight: 600,
        }}
      >
        {isLoggedIn ? "Open dashboard" : cta} <ArrowR />
      </Link>
    </div>
  )
}

function StickyCta({ isSample }: { isSample: boolean }) {
  // /try is reachable from anywhere — including the in-app shell after
  // someone is already signed in. The sign-up nudge makes no sense for
  // a logged-in visitor (they already have the account), and pointing
  // them at /login?mode=signup just bounces them straight back. When
  // we detect an authed session we collapse the bar into a "go run the
  // real thing" CTA aimed at /dashboard instead.
  const { user } = useAuth()
  const isLoggedIn = !!user

  const headline = isLoggedIn
    ? (isSample ? "Sample ready. Run your real list now." : "Your list is ready.")
    : (isSample ? "Sample ready. Want to try yours?"      : "Your list is ready.")

  const subline = isLoggedIn
    ? "Open the dashboard — full pipeline, MX, suppression, history."
    : (isSample
        ? "Sign up free · clean your real list · 1,000 rows / month included."
        : "Sign up free to download · keep this report · run lists up to 1,000 rows / month.")

  const ctaHref  = isLoggedIn ? "/dashboard"          : "/login?mode=signup"
  const ctaLabel = isLoggedIn
    ? "Open dashboard"
    : (isSample ? "Sign up — clean my list" : "Sign up — keep this report")

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-30"
      style={{
        background: "rgba(250,247,238,0.92)",
        backdropFilter: "blur(14px) saturate(140%)",
        WebkitBackdropFilter: "blur(14px) saturate(140%)",
        borderTop: `1px solid ${T.hair2}`,
        boxShadow: "0 -20px 60px -20px rgba(10,10,10,0.10)",
      }}
    >
      <div className={`${MAXW} mx-auto px-4 sm:px-6 lg:px-10 py-3 sm:py-4 flex flex-col sm:flex-row sm:items-center sm:gap-4 gap-3 sm:justify-between`}>
        <div className="flex items-center gap-3 min-w-0">
          <span
            className="inline-flex items-center justify-center flex-shrink-0"
            style={{
              width: 32, height: 32, borderRadius: 999,
              background: `linear-gradient(135deg, ${T.acc}, ${T.accDeep})`,
              color: "#FAF7EE",
              boxShadow: `0 0 20px ${T.accGlow}`,
            }}
          >
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
              <path d="M3 8.5l3.5 3L13 5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate" style={{ fontSize: 13, fontWeight: 700, letterSpacing: "-0.005em" }}>
              {headline}
            </p>
            <p className="truncate" style={{ fontSize: 11.5, color: T.ink2 }}>
              {subline}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 sm:ml-auto">
          <Link
            href={ctaHref}
            className="inline-flex items-center justify-center gap-1.5 w-full sm:w-auto"
            style={{
              background: T.acc, color: "#FAF7EE",
              padding: "10px 16px", borderRadius: 10,
              fontSize: 13.5, fontWeight: 700,
              boxShadow: `0 16px 40px -14px ${T.accGlow}`,
            }}
          >
            {ctaLabel}
            <ArrowR size={14} />
          </Link>
        </div>
      </div>
    </div>
  )
}

/* ───────────────────────────────────────────────────────────────────────────
   utils
   ─────────────────────────────────────────────────────────────────────────── */

function useCountUp(target: number, duration = 800) {
  const [v, setV] = useState(0)
  useEffect(() => {
    let raf = 0
    let start: number | null = null
    const step = (t: number) => {
      if (start == null) start = t
      const p = Math.min(1, (t - start) / duration)
      const eased = 1 - Math.pow(1 - p, 3)
      setV(target * eased)
      if (p < 1) raf = requestAnimationFrame(step)
    }
    raf = requestAnimationFrame(step)
    return () => cancelAnimationFrame(raf)
  }, [target, duration])
  return Math.round(v)
}
